import { DownOutlined, ExportOutlined, PlusOutlined } from '@ant-design/icons'
import { Button, Text } from '@lmc/ui-kit'
import type { MenuProps } from 'antd'
import { Dropdown } from 'antd'
import { NAMESPACE } from 'locale'
import { reportsConstants } from 'locale/team'
import type React from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

// CM-3388: on the group screen the Add-member control becomes a dropdown
// offering "Add to specific sites" vs "Add to Group Dashboard". When
// `addMemberOptions` is provided the header renders that menu; otherwise it
// falls back to the single-button behaviour TeamListV2 relies on (unchanged).
export type AddMemberOption = {
  key: string
  label: string
  onSelect: () => void
}

type TeamListHeaderV2Props = {
  canAddMember: boolean
  onAddMember?: () => void
  addMemberOptions?: AddMemberOption[]
  testId?: string
}

const StyledContainer = styled.div`
  background-color: ${(props) => props.theme.palette.white};
  padding: 24px;
  display: flex;
  align-items: center;
  gap: 24px;

  ${({ theme }) => theme.mediaQueries.mobileDown} {
    padding: 16px;
    gap: 16px;
  }
`

const StyledTitleBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
  min-width: 0;
`

const StyledTitle = styled.h1`
  font-size: 32px;
  line-height: 40px;
  font-weight: 700;
  letter-spacing: 0.24px;
  margin: 0;
  color: #1f1f1f;
`

const StyledSubtitleRow = styled(Text)`
  color: ${(props) => props.theme.color.neutral.color4};
  display: inline;
  line-height: 24px !important;
`

const StyledOverviewLink = styled.span`
  color: ${(props) => props.theme.color.status.primary};
  text-decoration: underline;
  margin-left: 4px;
  cursor: not-allowed;
  display: inline-flex;
  align-items: center;
  gap: 8px;
`

const StyledExternalLinkIcon = styled(ExportOutlined)`
  font-size: 16px;
`

const StyledAddMemberSlot = styled.div`
  flex-shrink: 0;

  ${({ theme }) => theme.mediaQueries.tabletDown} {
    display: none;
  }
`

const TeamListHeaderV2: React.FC<TeamListHeaderV2Props> = ({
  canAddMember,
  onAddMember,
  addMemberOptions,
  testId
}) => {
  const { t } = useTranslation(NAMESPACE.TEAM)
  const hasMenu = Boolean(addMemberOptions?.length)
  const menuItems: MenuProps['items'] = (addMemberOptions ?? []).map((opt) => ({
    key: opt.key,
    label: opt.label,
    onClick: opt.onSelect,
    'data-testid': testId ? `${testId}-add-member-${opt.key}` : undefined
  }))

  return (
    <StyledContainer data-testid={testId}>
      <StyledTitleBlock>
        <StyledTitle data-testid={testId ? `${testId}-title` : undefined}>
          {t(reportsConstants.TEAM_LIST_TITLE)}
        </StyledTitle>
        <StyledSubtitleRow level={3}>
          {t(reportsConstants.TEAM_LIST_SUBTITLE)}
          {/* TODO(CM-3387): swap to an active link once the overview URL exists. */}
          <StyledOverviewLink
            role="link"
            aria-disabled="true"
            data-testid={testId ? `${testId}-overview-link` : undefined}
          >
            <span>{t(reportsConstants.TEAM_LIST_OVERVIEW_LINK)}</span>
            <StyledExternalLinkIcon aria-hidden />
          </StyledOverviewLink>
        </StyledSubtitleRow>
      </StyledTitleBlock>
      {canAddMember ? (
        <StyledAddMemberSlot>
          {hasMenu ? (
            <Dropdown
              menu={{ items: menuItems }}
              trigger={['click']}
              placement="bottomRight"
            >
              <Button
                variant="primary"
                icon={<PlusOutlined />}
                data-testid={testId ? `${testId}-add-member` : undefined}
              >
                {t(reportsConstants.TEAM_LIST_ADD_MEMBER)}
                <DownOutlined style={{ marginLeft: 8 }} />
              </Button>
            </Dropdown>
          ) : (
            <Button
              variant="primary"
              icon={<PlusOutlined />}
              onClick={onAddMember}
              data-testid={testId ? `${testId}-add-member` : undefined}
            >
              {t(reportsConstants.TEAM_LIST_ADD_MEMBER)}
            </Button>
          )}
        </StyledAddMemberSlot>
      ) : null}
    </StyledContainer>
  )
}

export default TeamListHeaderV2
